import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  EMITTER_CONSTANTS,
  ID_BOOKMARK_TAB,
  OTHER_EVENT_LIST,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_EVENT_OTHER,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import { convertToMomentJs, getEventTypeByGroupAction } from '@/shared/lib/aiUtils';
import {
  checkCouldRemoveStrip,
  calculateStartStopStripMomentOfListEvent,
  shouldApplyGroupChange,
  formatDisplayEvent,
  formatEventTitle,
  updateListEventRemovedStrip,
} from '@/entities/event';
import { useEmitter, useMergeState } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import {
  selectActiveTab,
  selectAiChannel,
  selectEventChanges,
  selectEventOthers,
  selectGroupEventChanges,
  selectIsLoadingEcgViewer,
  selectMinuteInfoEvents,
  selectSelectedEventInfo,
  selectSelectedEventInfoIndex,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { EventSelectedInfoProps } from '.';

/**
 * Backs the selected-event info panel that lists the events sharing the current minute and drives the strip editor. Merges pending event and group changes over the selected events, sorts them by relevance to the active tab, formats each row's title and times, and exposes handlers to open the strip modal, persist strip edits, remove a strip and jump the ECG viewer to an event's start or stop.
 *
 * @param props - Panel props supplying the Recoil key, the timezone offset and the ECG data map.
 * @returns An object carrying state, activeButton, isLoadingEcgViewer, formatSelectedEvent, formatSelectedEventInfo, toggleEditAiStripModal, updateEditData, handleRemoveStrip, onChangeSelectedEvent and onClickJumpTo.
 */
const useEventSelectedInfo = ({
  keyRecoil,
  timezoneOffset,
  ecgDataMap,
}: Pick<EventSelectedInfoProps, 'keyRecoil' | 'timezoneOffset' | 'ecgDataMap'>) => {
  const [state, setState] = useMergeState<{
    isVisibleEditAiStripModal: boolean;
    isLoadingSaveAiReport: boolean;
    selectedData?: any;
    originalSelectedData?: any;
    couldRemoveStrip?: any;
    events?: any[];
    isOpenConfirmNavigateTab?: any;
  }>({
    isVisibleEditAiStripModal: false,
    isLoadingSaveAiReport: false,
  });

  const selectedEventIndex = useReportStore(selectSelectedEventInfoIndex(keyRecoil));
  const setSelectedEventIndexAction = useReportStore((s) => s.actions.setSelectedEventInfoIndex);
  /**
   * Records which row of the selected-event list is active. Forwards the value to the store action with the key already bound.
   *
   * @param v - The row index, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setSelectedEventIndex = (v: Parameters<typeof setSelectedEventIndexAction>[1]) =>
    setSelectedEventIndexAction(keyRecoil, v);
  const setSelectedEventAction = useReportStore((s) => s.actions.setSelectedEvent);
  /**
   * Publishes the event currently highlighted in the info panel. Forwards the value to the store action with the key already bound.
   *
   * @param v - The selected event, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setSelectedEvent = (v: Parameters<typeof setSelectedEventAction>[1]) =>
    setSelectedEventAction(keyRecoil, v);
  const eventOthers = useReportStore(selectEventOthers(keyRecoil));
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Writes the list of other-type events and their captures for this Recoil key. Forwards the value to the store action with the key already bound.
   *
   * @param v - The new other-events list, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  const minuteInfoEvs = useReportStore(selectMinuteInfoEvents(keyRecoil));
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  /**
   * Reads the pending edits to existing ECG events for this Recoil key. Runs selectEventChanges against a getState snapshot so a handler sees writes made moments earlier.
   *
   * @returns The current list of changed events.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending edits to existing events for this Recoil key back into the report store. Forwards the value to the store action with the key already bound.
   *
   * @param v - The new list of changed events, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Sets the window size in seconds the ECG viewer centres on after a jump. Forwards the value to the store action with the key already bound.
   *
   * @param v - The portion in seconds, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets the date the ECG viewer should load before jumping. Forwards the value to the store action with the key already bound.
   *
   * @param v - The target epoch in milliseconds, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setOpenModalEditStripAction = useReportStore((s) => s.actions.setIsOpenModalEditStrip);
  /**
   * Records whether the strip edit modal is open so other panels can react. Forwards the value to the store action with the key already bound.
   *
   * @param v - True while the edit modal is open, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setOpenModalEditStrip = (v: Parameters<typeof setOpenModalEditStripAction>[1]) =>
    setOpenModalEditStripAction(keyRecoil, v);
  const selectedEventInfo = useReportStore(selectSelectedEventInfo(keyRecoil));
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Sets the timestamp the ECG viewer scrolls to inside the loaded window. Forwards the value to the store action with the key already bound.
   *
   * @param v - The target epoch in milliseconds, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const isLoadingEcgViewer = useReportStore(selectIsLoadingEcgViewer(keyRecoil));
  const setPreventReloadEcgAction = useReportStore((s) => s.actions.setPreventReloadEcg);
  /**
   * Suppresses the ECG viewer reload that would otherwise follow a store write. Forwards the value to the store action with the key already bound.
   *
   * @param v - True to block the next reload, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setPreventReloadEcg = (v: Parameters<typeof setPreventReloadEcgAction>[1]) =>
    setPreventReloadEcgAction(keyRecoil, v);
  const setReloadEcgViewerAction = useReportStore((s) => s.actions.setReloadEcgViewer);
  /**
   * Bumps the counter the ECG viewer watches to force a reload. Forwards the value to the store action with the key already bound.
   *
   * @param v - The new counter value, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setReloadEcgViewer = (v: Parameters<typeof setReloadEcgViewerAction>[1]) =>
    setReloadEcgViewerAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the combined event options snapshot that undo and redo replay from. Forwards the value to the store action with the key already bound.
   *
   * @param v - The new event options object, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);

  const keepingEventId = useRef(null);
  const updatingTabCount = useRef(null);
  const lastStripId = useRef(null);

  // selectedEventInfoState (Recoil/index.ts, Task 7) is typed IEvent | IPoint, but this file reads/writes
  // event-only fields (idEvent, type, isReviewed, originalData, ...) that IPoint lacks - pre-existing gap.
  const mergeSlectedEventInfo = useMemo(
    () =>
      _.map(selectedEventInfo, (event: any) => {
        const updatedEvent: any = _.find(eventChanges, (x: any) => x.idEvent === event.idEvent);
        if (updatedEvent) {
          return updatedEvent;
        }
        const groupUpdatedEvent: any = _.find(groupEventChanges, (x: any) =>
          shouldApplyGroupChange(event, x),
        );
        if (groupUpdatedEvent) {
          return {
            ...event,
            type: getEventTypeByGroupAction(groupUpdatedEvent.actionType) || event.type,
            isReviewed:
              groupUpdatedEvent.actionType === 'markedAsReviewed'
                ? true
                : groupUpdatedEvent.actionType === 'markedAsUnreviewed'
                  ? false
                  : event.isReviewed,
          };
        }
        return event?.originalData || event;
      }),
    [selectedEventInfo, eventChanges, groupEventChanges],
  );

  /**
   * Opens or closes the AI strip edit modal and loads the strip it should show. Flips the visibility flag, assembles the selected strip from the event and capture pair with the timezone offset, comment and capture id, resolves whether the strip may be removed, looks up the untouched original for the reset action, and publishes the open state to the store.
   *
   * @param editData - The event whose strip is being edited; omitted when closing.
   * @param capture - The capture belonging to that event, supplying the comment, capture id and start.
   * @returns Nothing.
   */
  const toggleEditAiStripModal = (editData?: any, capture?: any) => {
    const couldRemoveStrip = checkCouldRemoveStrip(editData);
    setState({
      isVisibleEditAiStripModal: !state.isVisibleEditAiStripModal,
      selectedData: {
        ...editData,
        ...capture,
        timezoneOffset,
        comments: capture?.technicianComments || '',
        id: editData?.id,
        stripId: capture?.captureId,
        event: editData,
        start: editData?.start || capture?.start,
      },
      couldRemoveStrip,

      originalSelectedData: editData
        ? _.find(state.events, (x: any) => x.id === editData.id)
        : undefined,
    });
    setOpenModalEditStrip(!!editData);
  };

  /**
   * Persists an edited strip annotation onto either the other-events list or the event changes list. For other events it updates or clones the matching entry's captures with the new comment and title, otherwise it blocks the ECG reload, marks the capture as an update and dedupes it onto the event; both paths route through event options so undo and redo can replay, and the modal closes unless told otherwise.
   *
   * @param updateData - The edited annotation carrying comments, title, stripId and originalEventId.
   * @param needCloseModal - Closes the edit modal after the write when true; defaults to true.
   * @param type - The event type; the other-event branch runs only when it matches the other-event constant.
   * @returns Nothing.
   */
  const updateEditData = (updateData: any = {}, needCloseModal = true, type = '') => {
    const { comments, title, stripId, originalEventId } = updateData || {};
    if (type === TYPE_EVENT_OTHER) {
      // eventOthersState (RecoilEffect/recoilBeat, Task 7) is typed IEventOther, but this file reads/writes
      // captures/originalEventId/isUpdatedOther/isRemovedStrip fields IEventOther lacks - pre-existing gap.
      const newEventOthers: any[] = _.cloneDeep(eventOthers);
      const index = _.findIndex(newEventOthers, (x: any) => x.captures?.[0]?.captureId === stripId);
      if (index > -1) {
        newEventOthers[index] = {
          ...newEventOthers[index],
          technicianComments: comments,
          title,
          isUpdatedOther: true,
          captures: [
            {
              ...newEventOthers[index].captures[0],
              technicianComments: comments,
              comment: comments,
            },
          ],
        };
      } else {
        const newEvent: any = _.find(
          minuteInfoEvs,
          (x: any) => x.originalEventId === originalEventId,
        );
        if (newEvent) {
          const cloneEvent: any = _.cloneDeep(newEvent);
          const cloneCapture: any = _.cloneDeep(cloneEvent.captures[0]);
          cloneCapture.technicianComments = comments;
          cloneCapture.comment = comments;
          cloneEvent.technicianComments = comments;
          cloneEvent.title = title;
          cloneEvent.captures = [cloneCapture];
          cloneEvent.isUpdatedOther = true;
          newEventOthers.push(cloneEvent);
        }
      }
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventOthersState: newEventOthers,
      }));
      setEventOthers(newEventOthers as any);
    } else {
      setPreventReloadEcg(true);
      const updatedEvent: any = _.find(
        mergeSlectedEventInfo,
        (x: any) => x.idEvent === originalEventId,
      );
      if (updatedEvent) {
        const updatedCapture = _.find(updatedEvent?.captures, (x: any) => x.captureId === stripId);
        const cloneUpdatedCapture: any = _.cloneDeep(updatedCapture);
        if (!cloneUpdatedCapture?.isNewCapture) {
          _.assign(cloneUpdatedCapture, { action: 'update' });
        }
        _.assign(cloneUpdatedCapture, { technicianComments: comments });
        const newUpdatedEvent = {
          ...updatedEvent,
          captures: _.uniqBy([cloneUpdatedCapture, ...updatedEvent.captures], 'captureId'),
          isUpdateImage: true,
        };
        const events = _.uniqBy([newUpdatedEvent, ...getEventChanges()], 'idEvent');
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventChangesState: events,
        }));
        setEventChanges(events);
      }
    }
    if (needCloseModal) {
      toggleEditAiStripModal();
    }
  };

  const formatSelectedEventInfo = useMemo(() => {
    const sortedSelectedEventInfo = _.sortBy(mergeSlectedEventInfo, [
      (event) => event.type !== activeButton,
      (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.AFIB,
      (event) => !OTHER_EVENT_LIST.includes(event.type),
      (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT,
      (event) => event?.start,
      (event) => !event?.type,
    ]);
    let filterData: any[] = sortedSelectedEventInfo || [];
    // selectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark; this file reads
    // event-only fields (idEvent, isBookmarkStrip, ...) that IBeat/IEcgBookmark lack - pre-existing gap.
    const selectedStripAny = selectedStrip as any;
    if (selectedStripAny && !_.isEmpty(sortedSelectedEventInfo)) {
      const selectedStripIndex = selectedStripAny?.idEvent
        ? _.findIndex(sortedSelectedEventInfo, (x: any) => x.idEvent === selectedStripAny?.idEvent)
        : -1;
      if (selectedStripIndex > 0) {
        filterData = _.sortBy(sortedSelectedEventInfo, [
          (event: any) => event.idEvent !== selectedStripAny?.idEvent,
        ]);
      }
    }
    const minuteInfo = filterData?.[filterData.length - 1];

    if (
      (activeButton as any) === ID_BOOKMARK_TAB &&
      selectedStripAny?.isBookmarkStrip &&
      !_.isEmpty(filterData) &&
      lastStripId.current !== selectedStripAny?.id &&
      minuteInfo.startTimeEpoch === selectedStripAny?.startTimeEpoch
    ) {
      lastStripId.current = selectedStripAny?.id;
      setSelectedEventIndex(filterData.length - 1);
    }
    const formattedColumnInfo = _.map(filterData, (event: any, index: any) => {
      const type =
        event?.type === TYPE_BEAT_ENUM.DELETED ? event?.oldType : event?.type || event?.oldType;
      let stopTime;
      if (event?.stop) {
        const stopMoment = convertToMomentJs(event.stop, timezoneOffset);
        stopTime = stopMoment.startOf('minute').format('MM/DD/YYYY, HH:mm:ss');
      }

      return {
        event,
        // formatEventTitle (./helper.ts) only declares (event, data) - this call site's 3rd arg (index) is unused, pre-existing.
        titleFormat: (formatEventTitle as any)(event, filterData, index),
        index,
        type: type || 'MINUTE_INFO',
        startTime: !_.isNil(event?.startTimeEpoch)
          ? convertToMomentJs(event?.startTimeEpoch, timezoneOffset)?.format('MM/DD/YYYY, HH:mm:ss')
          : '',
        stopTime,
        isShowButton: type,
        eventOthers: !type || event.type === TYPE_EVENT_OTHER ? eventOthers : undefined,
        minuteInfoEvs,
      };
    });
    return formattedColumnInfo;
  }, [
    mergeSlectedEventInfo,
    timezoneOffset,
    eventOthers,
    minuteInfoEvs,
    selectedStrip,
    activeButton,
  ]);

  /**
   * Removes a captured strip from the report, either as an other-event entry or as an edit to an existing event. Flags or clones the matching other-event, or drops the entry outright when only a capture id is known, and otherwise marks the strip removed on the event changes list; both paths write through event options for undo and redo, close the modal and re-emit the capture update.
   *
   * @param data - The strip being removed, carrying its type, originating event id and capture id.
   * @returns Nothing.
   */
  const handleRemoveStrip = (data: any) => {
    if (data?.type === TYPE_EVENT_OTHER) {
      if (data.originalEventId) {
        const newEventOthers: any[] = _.cloneDeep(eventOthers);
        const updatedEvent: any = _.find(
          newEventOthers,
          (x: any) => x.originalEventId === data.originalEventId,
        );
        if (updatedEvent) {
          _.assign(updatedEvent, { isRemovedStrip: true });
        } else {
          const newEvent: any = _.find(
            minuteInfoEvs,
            (x: any) => x.originalEventId === data.originalEventId,
          );
          if (newEvent) {
            const cloneEvent: any = _.cloneDeep(newEvent);
            cloneEvent.isRemovedStrip = true;
            newEventOthers.push(cloneEvent);
          }
        }
        // Perform grouping event action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventOthersState: newEventOthers,
        }));
        setEventOthers(newEventOthers as any);
      } else {
        const newEventOthers: any[] = _.cloneDeep(eventOthers);
        _.remove(newEventOthers, (x: any) => x?.captures?.[0]?.captureId === data?.stripId);
        // Perform grouping event action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventOthersState: newEventOthers,
        }));
        setEventOthers(newEventOthers as any);
      }

      toggleEditAiStripModal();
    } else {
      setPreventReloadEcg(true);
      const events = updateListEventRemovedStrip(getEventChanges(), data, { isRemove: true });
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventChangesState: events,
      }));
      setEventChanges(events);
      toggleEditAiStripModal();
    }
    setTimeout(() => {
      emitter.emit(EMITTER_CONSTANTS.UPDATE_CAPTURE_EVENT);
    }, 10);
  };

  /**
   * Moves the ECG viewer to the event at the given row of the formatted list. Jumps to the event start with a two- or ten-second portion when the event begins inside the displayed minute, otherwise falls back to the minute epoch with no portion, and skips the jump entirely on the bookmark tab when asked to check for it.
   *
   * @param index - Row index into the formatted selected-event list.
   * @param checkIsBookMark - When set, suppresses the jump while the bookmark tab is active.
   * @returns Nothing.
   */
  const handelJumpToEvent = useCallback(
    (index: any, checkIsBookMark?: any) => {
      if (checkIsBookMark && (activeButton as any) === ID_BOOKMARK_TAB) return;
      const selectedEvent = formatSelectedEventInfo[index]?.event;
      const isSameMinute = moment(selectedEvent?.start).isSame(
        moment(selectedEvent?.startTimeEpoch),
        'minute',
      );
      if (isSameMinute && selectedEvent?.start && !!selectedEvent?.type) {
        setJumpTime(moment(selectedEvent?.start).valueOf());
        const portion = (TYPE_BEAT_EVENT_ENUM as any)[selectedEvent?.type] ? 2 : 10;
        setJumpPortion(portion);
      } else {
        setJumpPortion(0);
        setJumpTime(selectedEvent?.startTimeEpoch);
      }
    },
    [formatSelectedEventInfo, activeButton],
  );

  /**
   * Selects a different event row in the info panel and re-syncs the ECG viewer. Stores the index, bumps the viewer reload counter by a random amount to force a refresh, and jumps to the event on the next tick.
   *
   * @param index - Row index of the newly selected event.
   * @returns Nothing.
   */
  const onChangeSelectedEvent = (index: any) => {
    setSelectedEventIndex(index);
    // reload to get new
    setReloadEcgViewer((prev) => prev + _.round(Math.random() * 100));
    setTimeout(() => {
      handelJumpToEvent(index);
    }, 10);
  };

  /**
   * Jumps the ECG viewer to the start or the stop of one listed event. Picks the timestamp for the requested edge and scrolls within the loaded window when it falls in the displayed minute or inside the strip range, otherwise remembers the event id and jumps the whole date; the portion is two seconds for stop edges and beat events, ten otherwise.
   *
   * @param info - The formatted row whose event supplies the timestamps.
   * @param type - Edge to jump to, either start or stop; defaults to start.
   * @returns Nothing.
   */
  const onClickJumpTo = (info: any, type = 'start') => {
    let jumpMoment;
    if (type === 'stop') {
      jumpMoment = info?.event?.stop
        ? moment(info?.event?.stop)
        : info?.event?.stopTimeEpoch
          ? moment(info?.event?.stopTimeEpoch)
          : null;
    } else {
      jumpMoment = info?.event?.start
        ? moment(info?.event?.start)
        : info?.event?.startTimeEpoch
          ? moment(info?.event?.startTimeEpoch)
          : null;
    }
    const portion = type === 'stop' || (TYPE_BEAT_EVENT_ENUM as any)[info?.event?.type] ? 2 : 10;
    const minuteInfo = formatSelectedEventInfo?.[formatSelectedEventInfo.length - 1];
    const isSameMinute =
      jumpMoment && moment(minuteInfo?.event?.startTimeEpoch).isSame(jumpMoment, 'minute');

    // Calculate start and stop strip moment of the list event (+ or - 10 seconds)
    const { startStrip, stopStrip } = calculateStartStopStripMomentOfListEvent({
      events: selectedEventInfo,
    });

    const isBetweenTime =
      jumpMoment && jumpMoment.isBetween(startStrip, stopStrip, undefined, '[]');
    if ((isSameMinute || isBetweenTime) && jumpMoment) {
      setJumpTime(jumpMoment.valueOf());
    } else if (jumpMoment) {
      keepingEventId.current = info?.event?.id || info?.event?.eventId;
      setJumpDate(jumpMoment.valueOf());
    }
    setJumpPortion(portion);
  };

  const formatSelectedEvent: any = useMemo(() => {
    if (_.isEmpty(formatSelectedEventInfo)) return {};
    const count = formatSelectedEventInfo.length;
    let index;
    if (keepingEventId.current) {
      const savedIndex = _.findIndex(
        formatSelectedEventInfo,
        (x: any) =>
          x.event?.id === keepingEventId.current || x.event?.eventId === keepingEventId.current,
      );
      setSelectedEventIndex(savedIndex > -1 ? savedIndex : 0);
      index = savedIndex > -1 ? savedIndex : 0;
      keepingEventId.current = null;
    } else {
      index = selectedEventIndex > count - 1 ? 0 : selectedEventIndex || 0;
    }
    const data = formatSelectedEventInfo[index];
    const minuteInfo = formatSelectedEventInfo?.[formatSelectedEventInfo.length - 1];
    setSelectedEvent(data?.event);
    return {
      ...data,
      displayEvent: formatDisplayEvent(data, aiChannel, timezoneOffset, ecgDataMap),
      slectedIindex: index,
      startTime: minuteInfo?.startTime,
      stopTime: minuteInfo?.stopTime,
    };
  }, [selectedEventIndex, formatSelectedEventInfo, aiChannel, timezoneOffset, ecgDataMap]);

  useEffect(() => {
    setSelectedEventIndex(0);
  }, [activeButton]);

  useEmitter(
    EMITTER_CONSTANTS.AI_LOADING,
    (msg: any) => {
      const { isLoading } = msg || {};
      if (isLoading) {
        updatingTabCount.current = 0;
        setState({ isLoadingSaveAiReport: true });
      } else if (updatingTabCount.current > 0) {
        updatingTabCount.current -= 1;
        if (updatingTabCount.current === 0) {
          setState({ isLoadingSaveAiReport: false });
        }
      } else {
        updatingTabCount.current = 0;
        setState({ isLoadingSaveAiReport: false });
      }
    },
    [state.isOpenConfirmNavigateTab],
  );

  return {
    state,
    activeButton,
    isLoadingEcgViewer,
    formatSelectedEvent,
    formatSelectedEventInfo,
    toggleEditAiStripModal,
    updateEditData,
    handleRemoveStrip,
    onChangeSelectedEvent,
    onClickJumpTo,
  };
};

export default useEventSelectedInfo;
